/* DateField — Consistente cross-browser datumpicker gebouwd op shadcn Calendar
 * + Popover. Vervangt <Input type="date"> zodat de picker overal (Chrome,
 * Safari, Firefox) hetzelfde uitziet en de demo-polish behoudt.
 *
 * Props:
 *   value      : ISO datum-string 'YYYY-MM-DD' (of leeg)
 *   onChange   : (nextIsoDate: string) => void  — leeg als user wist
 *   min / max  : optionele ISO-strings die selectie beperken
 *   placeholder: label wanneer waarde leeg is
 *   disabled   : boolean
 *   className  : optionele extra classes voor de trigger-knop
 *   data-testid: door-doorgegeven aan de trigger-knop
 */
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPrimitive } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";

const _fmt = (d) => {
  if (!d) return "";
  // Local ISO (geen UTC-drift over tijdzones)
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const _parseIso = (s) => {
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return undefined;
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

export default function DateField({
  value,
  onChange,
  min,
  max,
  placeholder = "Kies datum",
  disabled = false,
  className = "",
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const date = useMemo(() => _parseIso(value), [value]);
  const minDate = useMemo(() => _parseIso(min), [min]);
  const maxDate = useMemo(() => _parseIso(max), [max]);

  const disabledMatcher = useMemo(() => {
    const rules = [];
    if (minDate) rules.push({ before: minDate });
    if (maxDate) rules.push({ after: maxDate });
    return rules.length ? rules : undefined;
  }, [minDate, maxDate]);

  const label = date
    ? date.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "h-9 justify-start text-left font-normal w-full",
            !date && "text-neutral-500",
            className
          )}
          {...rest}
        >
          <CalendarIcon className="h-4 w-4 mr-2 text-neutral-400" />
          <span className="flex-1 truncate">{label}</span>
          {date && !disabled && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Datum wissen"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onChange(""); } }}
              className="ml-2 opacity-60 hover:opacity-100 hover:text-red-600"
              data-testid={`${rest["data-testid"] || "date-field"}-clear`}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-white z-[300]"
        data-testid={`${rest["data-testid"] || "date-field"}-popover`}
      >
        <CalendarPrimitive
          mode="single"
          selected={date}
          onSelect={(d) => {
            onChange(_fmt(d));
            setOpen(false);
          }}
          initialFocus
          disabled={disabledMatcher}
          defaultMonth={date || new Date()}
          weekStartsOn={1}
          fromDate={minDate}
          toDate={maxDate}
        />
      </PopoverContent>
    </Popover>
  );
}
